import React, { Component } from "react";
import { connect } from "react-redux";
import Col from "react-bootstrap/lib/Col";
import { bool, func, string } from "prop-types";
import Yup from "yup";
import Button from "pages/_components/Button";
import { Form, Field, withFormik } from "formik";
import { compose } from "redux";
import classNames from "classnames";

import * as i18nUtils from "util/i18n";

import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import Container from "pages/_components/Container";
import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import MainContainer from "pages/_components/MainContainer";
import {
    actions as frequentDestinationActions,
    selectors as frequentDestinationSelector,
} from "reducers/frequentDestination";
import { routerActions } from "react-router-redux/actions";
import Credential from "pages/_components/fields/credentials/Credential";
import Heading from "pages/_components/Heading";
import isTokenActive from "util/token";
import { AgendaConfirm } from "@ui-kit/components/index.es";
import {
    dispatchV3ShellLayout,
    isUxDashboardImprovementsEnabled,
    isContactListUxEnabled,
} from "util/v3ShellLayoutSync";
import { actions as layoutActions } from "reducers/layout";
import { selectors as statusSelectors } from "reducers/status";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import { resolveFrequentDestinationGoBackPath } from "pages/frequentDestinations/_utils/resolveGoBackPath";
import {
    buildFrequentDestinationConfirmFields,
    buildAgendaSoftTokenConfirmProps,
    otpFormikBag,
} from "pages/frequentDestinations/_utils/frequentDestinationSummary";

const FORM_ID = "createFrequentDestination";

class CreateFrequentDestinationConfirmations extends Component {
    static propTypes = {
        fetching: bool.isRequired,
        isDesktop: bool.isRequired,
        frequentDestination: {},
        dispatch: func.isRequired,
        alias: string,
        idTransaction: string,
        lastHrefPathname: string,
        tokenNumber: string,
    };

    static defaultProps = {
        frequentDestination: {},
        alias: null,
        idTransaction: null,
        lastHrefPathname: "",
        tokenNumber: null,
    };

    componentDidMount() {
        const { frequentDestination, dispatch, lastHrefPathname, isDesktop } = this.props;

        if (!frequentDestination) {
            dispatch(routerActions.goBack());
            return;
        }
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
        dispatchV3ShellLayout(dispatch, {
            titleKey: "frequentDestination.create.newFrequentDestination",
            // Fallback if handler is cleared; prefer history back (see setGoBackHandler below).
            goBackPath: resolveFrequentDestinationGoBackPath(lastHrefPathname),
            rightContentMobile: null,
            scopes: isContactListUxEnabled()
                ? ["frequentDestination", "frequentDestinationCreateConfirm", "agenda"]
                : ["frequentDestination"],
        });
        // Mobile V3 shell uses goBackPath, but lastHref stays on the agenda list (create form
        // never saveLastHref). Desktop PageHeader calls goBack() — mirror that on mobile.
        dispatch(layoutActions.setGoBackHandler(this.handleBack));
    }

    componentWillUnmount() {
        const { dispatch, lastHrefPathname } = this.props;
        dispatch(layoutActions.setGoBackHandler(null));
        dispatchV3ShellLayout(dispatch, {
            titleKey: "",
            goBackPath: resolveFrequentDestinationGoBackPath(lastHrefPathname),
            rightContentMobile: null,
            scopes: null,
        });
    }

    handleBack = () => {
        const { dispatch } = this.props;
        dispatch(routerActions.goBack());
    };

    handleClose = () => {
        const { dispatch } = this.props;
        dispatch(routerActions.go(-2));
    };

    centerContentMobile = () => (
        <div className="title-account-header-multiline-background-blue d-flex justify-content-center">
            <h1>{i18nUtils.get("frequentDestination.create.newFrequentDestination")}</h1>
        </div>
    );

    renderV3 = () => {
        const { fetching, frequentDestination, alias, dispatch, idTransaction, tokenNumber, isDesktop } = this.props;
        const softTokenProps = buildAgendaSoftTokenConfirmProps(tokenNumber, isDesktop);
        return (
            <AgendaConfirm
                key={softTokenProps.confirmKey}
                styleV3
                title={i18nUtils.get("frequentDestination.create.newFrequentDestination")}
                goBack={this.handleBack}
                fields={buildFrequentDestinationConfirmFields(frequentDestination, { alias })}
                loading={fetching}
                getToken={softTokenProps.getToken}
                confirmationText={softTokenProps.confirmationText}
                validationConfirm={softTokenProps.validationConfirm}
                onConfirm={(values) => {
                    const otp = softTokenProps.resolveOtp(values);
                    if (!idTransaction) {
                        // CREATE_REQUEST sets fetching; CREATE_FAILURE clears it (ARBCMFIMP-20037)
                        dispatch(
                            frequentDestinationActions.frequentDestinationCreateRequest(
                                frequentDestination,
                                otp,
                                otpFormikBag(dispatch, ["frequentDestinationCreateConfirm", "agenda"]),
                            ),
                        );
                    }
                }}
            />
        );
    };

    render() {
        if (isContactListUxEnabled()) {
            const uxDashboard = isUxDashboardImprovementsEnabled();
            return (
                <>
                    {!uxDashboard ? <Notification scopeToShow="frequentDestinationCreateConfirm" /> : null}
                    {this.renderV3()}
                </>
            );
        }
        const { fetching, frequentDestination, alias, isDesktop } = this.props;
        const uxDashboard = isUxDashboardImprovementsEnabled();
        const containerClassName = `transfer-data data-wrapper-flex ${
            !isDesktop ? "space-between mb-0 container-details" : "mb-2"
        }`;
        const labelClassName = "font-weight-600 font-light";
        let bankNameInfo = frequentDestination.localBank;
        if (!frequentDestination.cbu?.startsWith("000")) {
            bankNameInfo += `/ ${frequentDestination.subsidiaryBank}`;
        }
        return (
            <>
                {!uxDashboard ? <Notification scopeToShow="frequentDestinationCreateConfirm" /> : null}
                <div className="admin-detail-head px-0 rigth-position-desktop">
                    {(!uxDashboard || isDesktop) && (
                        <Head
                            onBack={this.handleBack}
                            onClose={this.handleClose}
                            headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                            centerElement={isDesktop ? undefined : this.centerContentMobile}
                        />
                    )}
                    {isDesktop && (
                        <div className="view-title mb-4">
                            <h1>{i18nUtils.get("frequentDestination.create.newFrequentDestination")}</h1>
                        </div>
                    )}
                </div>
                <MainContainer showLoader={fetching}>
                    <Form className={!isDesktop ? "above-the-fold" : "confirmation__container"}>
                        <Container
                            className={classNames({
                                "pt-3 align-items-center flex-grow cmf-container-white": !isDesktop,
                            })}
                            gridClassName={!isDesktop ? "form-content" : "confirmation__form confirmation__box"}
                            rowClassName={!isDesktop && "align-items-center f-dir-col"}>
                            <div className={classNames("admin-content-center", { "mb-3": !isDesktop })}>
                                <I18n
                                    id="frequentDestination.create.confirmate.subtitle"
                                    component={!isDesktop ? "h2" : "h1"}
                                    componentProps={!isDesktop && { className: "font-size-20-px mx-3" }}
                                />
                            </div>
                            <Col sm={12} md={9} lg={12} className={`col col-12 ${!isDesktop && "px-0"}`}>
                                <Col sm={12} className="col col-12 px-0">
                                    <div className="d-flex justify-content-center">
                                        <Col className={`col col-12 ${isDesktop && "px-0"}`} sm={12} md={8} lg={12}>
                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label="frequentDestination.create.confirmate.numberAccount"
                                                labelClassName={labelClassName}
                                                data={<span>{`${frequentDestination.accountNumber}`}</span>}
                                            />
                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label="frequentDestination.create.confirmate.accountType"
                                                labelClassName={labelClassName}
                                                data={<span>{`${frequentDestination.productType}`}</span>}
                                            />

                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label="frequentDestination.create.confirmate.own"
                                                labelClassName={labelClassName}
                                                data={
                                                    <span>{`${frequentDestination.isOwn === "1" ? "SI" : "NO"}`}</span>
                                                }
                                            />

                                            {alias && (
                                                <Heading.DataGroup
                                                    containerClassName={containerClassName}
                                                    label="frequentDestination.create.confirmate.alias"
                                                    labelClassName={labelClassName}
                                                    data={<span>{`${alias}`}</span>}
                                                />
                                            )}
                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label="frequentDestination.create.confirmate.cbu"
                                                labelClassName={labelClassName}
                                                data={<span>{`${frequentDestination.cbu}`}</span>}
                                            />

                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label="frequentDestination.create.confirmate.document"
                                                labelClassName={labelClassName}
                                                data={
                                                    <span>
                                                        {`${frequentDestination.recipientDocumentType}
                                                    ${frequentDestination.recipientDocumentNumber}
                                                    `}
                                                    </span>
                                                }
                                            />
                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label="frequentDestination.create.confirmate.recipientName"
                                                labelClassName={labelClassName}
                                                data={<span>{`${frequentDestination.recipientName}`}</span>}
                                            />
                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label={`frequentDestination.create.confirmate.${
                                                    frequentDestination.cbu?.startsWith("000") ? "entity" : "bank"
                                                }`}
                                                labelClassName={labelClassName}
                                                data={<span>{bankNameInfo}</span>}
                                            />
                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label="frequentDestination.create.confirmate.email"
                                                labelClassName={labelClassName}
                                                data={<span>{frequentDestination.recipientEmail}</span>}
                                            />
                                            <Heading.DataGroup
                                                containerClassName={containerClassName}
                                                label="frequentDestination.create.confirmate.referent"
                                                labelClassName={labelClassName}
                                                data={<span>{`${frequentDestination.name}`}</span>}
                                            />
                                        </Col>
                                    </div>
                                </Col>
                                <Col sm={12} className="col col-12 d-flex justify-content-center">
                                    <Col className="col col-12" sm={12} md={8} lg={12}>
                                        <div className="admin-content-center mt-3">
                                            {!isTokenActive(!isDesktop) ? (
                                                <I18n
                                                    id="transfer.summary.confirm.label"
                                                    componentProps={{ className: "account-dropdown-header-font" }}
                                                />
                                            ) : (
                                                <div className="admin-content-center pb-3">
                                                    <I18n
                                                        id="confirmation.withoutotp.text"
                                                        component="p"
                                                        componentProps={{ className: "confirmation__text-without-otp" }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Col>
                            </Col>
                            <Col sm={12} md={6} lg={12} className="col col-12">
                                <Field
                                    placeholderText={i18nUtils.get(`confirmation.otp.placeholder`)}
                                    name="otp"
                                    type="otp"
                                    idForm={FORM_ID}
                                    component={Credential}
                                />
                            </Col>
                            {isDesktop && (
                                <Container className="flex-grow align-items-center w-100" gridClassName="form-content">
                                    <Col lg={12} md={12} sm={12} className="px-0">
                                        <Col className="confirmation__content-button" lg={12} md={12} sm={12}>
                                            <div className="admin-content-center">
                                                <Button
                                                    type="submit"
                                                    bsStyle="primary"
                                                    label="global.confirm"
                                                    loading={fetching}
                                                    disabled={fetching}
                                                    image="images/arrowRight.svg"
                                                    imageStyle="mr-2"
                                                    className="text-capitalize"
                                                />
                                            </div>
                                        </Col>
                                    </Col>
                                </Container>
                            )}
                        </Container>

                        {!isDesktop && (
                            <Container
                                className={!isDesktop && "flex-grow align-items-center"}
                                gridClassName={
                                    !isDesktop ? "form-content" : "confirmation__form confirmation__box-transparent"
                                }>
                                <Col sm={12} md={6} lg={12} className={`col col-12 ${isDesktop && "px-0"}`}>
                                    <Button
                                        image="images/arrowRight.svg"
                                        imageStyle="mr-2"
                                        type="submit"
                                        bsStyle="primary"
                                        label="global.confirm"
                                        loading={fetching}
                                    />
                                </Col>
                            </Container>
                        )}
                    </Form>
                </MainContainer>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    credentialGroups: frequentDestinationSelector.getCredentialGroups(state),
    frequentDestination: frequentDestinationSelector.getFrequentDestination(state),
    alias: frequentDestinationSelector.getAlias(state),
    fetching: frequentDestinationSelector.getFetching(state),
    lastHrefPathname: statusSelectors.getPathname(state) || "",
    tokenNumber: tokenSelectors.getTokenNumber(state),
});

export default compose(
    connect(mapStateToProps),
    resizableRoute,
    withFormik({
        mapPropsToValues: () => ({ otp: "" }),
        validationSchema: () =>
            Yup.object().shape({
                otp: Yup.string().required(i18nUtils.get("accounts.new.account.otp.error.required")),
            }),
        handleSubmit: ({ otp }, formikBag) => {
            const { frequentDestination, idTransaction, dispatch } = formikBag.props;

            if (!idTransaction) {
                // CREATE_REQUEST sets fetching; CREATE_FAILURE clears it (ARBCMFIMP-20037)
                dispatch(
                    frequentDestinationActions.frequentDestinationCreateRequest(frequentDestination, otp, formikBag),
                );
            }
        },
    }),
)(CreateFrequentDestinationConfirmations);
